import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Typography, TextField, Snackbar, Select, MenuItem, FormControl, Box, Button, InputLabel, FormHelperText } from '@mui/material';
import { useState, useEffect } from 'react';
import { Icon } from "@iconify/react";
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { initiativeCreateOrUpdate, countryMetaData, solutionMetaData, initiativeGetById } from '../initiative.service';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Loader from '../../../../components/loader';
import RichTextEditor from '../../../../components/textEditor';
import '../initiative.css';

const baseUrl = process.env.REACT_APP_AZURE_IMAGE_BASE_URL
const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN

function InitiativeCreation() {

    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get("type");
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [country, setCountry] = useState('');
    const [openAlert, setOpenAlert] = useState(false);
    const [alert, setAlert] = useState('');
    const [responseMsg, setResponseMsg] = useState("");
    const [countryData, setCountryData] = useState([]);
    const [solutionsData, setSolutionsData] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const ALERT_DURATION = 2000;
    const [imagePath, setImagePath] = useState("");

    const alertClose = () => {
        setAlert('')
    }

    useEffect(() => {
        if (type === 'edit') {
            setEditMode(true);
        }
        countriesData()
        solutionData()
        fetchData()
    }, [type, editMode]);

    const initiativeSchema = z.object({
        countryId: z.string().min(1, { message: "Country is required" }).or(z.number()),
        solutionId: z.string().min(1, { message: "Solution is required" }).or(z.number()),
        label: z.string().min(1, { message: "Label is required" }),
        name: z.string().min(1, { message: "Initiative is required" }),
        initiativeLink: z.string().min(1, { message: "Initiative link is required" }),
        description: z.string()
            .transform((val) => val.replace(/<[^>]*>/g, '').trim())
            .refine((val) => val !== '', {
                message: 'Description is required',
            }),
    });

    const { handleSubmit, formState: { errors }, control, reset, setValue } = useForm({
        resolver: zodResolver(initiativeSchema), defaultValues: {
            description: '',
        },
    });

    const fetchData = async () => {
        if (editMode === true) {
            const getItem = initiativeGetById(id).then(async (res) => {
                let initiativeData = res.data
                setValue('countryId', initiativeData?.countryId ? initiativeData.countryId : '');
                setValue('solutionId', initiativeData?.solution.id ? initiativeData.solution.id : '');
                setValue('name', initiativeData?.name ? initiativeData.name : '');
                setValue('description', initiativeData?.description ? initiativeData.description : '');
                setValue('label', initiativeData?.label ? initiativeData.label : '');
                setValue('initiativeLink', initiativeData?.initiativeLink ? initiativeData.initiativeLink : '');

                if (initiativeData?.imageUrl) {
                    const imageUrl = `${baseUrl}/${initiativeData.imageUrl}?${sasToken}`;
                    setPreviewImage(imageUrl);
                    setImagePath(initiativeData.imageUrl);
                }
            })
        }
    };

    const onSubmit = async (data) => {
        try {
            const formData = new FormData();
            formData.append("countryId", data.countryId);
            formData.append("solutionId", data.solutionId);
            formData.append("label", data.label);
            formData.append("name", data.name);
            formData.append("description", data.description);
            formData.append("initiativeLink", data.initiativeLink);

            if (selectedFile) {
                formData.append("imageUrl", selectedFile);
            } else {
                formData.append("imageUrl", imagePath);
            }

            setLoading(true)
            initiativeCreateOrUpdate(formData, editMode, id).then((res) => {
                if (res.statusCode === 201 || res.statusCode === 200) {
                    setResponseMsg(res.message);
                    setAlert('true');
                    setOpenAlert(true)
                    setLoading(false)
                    setTimeout(() => {
                        editMode
                            ? navigate(`/initiativeView/${id}?type=edit`)
                            : navigate('/initiative');
                        alertClose();
                    }, ALERT_DURATION);
                } else if (res.statusCode === 404) {
                    setResponseMsg(res.message);
                    setAlert('false');
                    setOpenAlert(true)
                    setLoading(false)
                    setTimeout(() => {
                        alertClose();
                    }, ALERT_DURATION);
                } else if (res.statusCode === 500 || res.statusCode === 400 || res.statusCode === 429) {
                    setResponseMsg(res.message);
                    setAlert('false');
                    setOpenAlert(true)
                    setLoading(false)
                    setTimeout(() => {
                        alertClose();
                    }, ALERT_DURATION);
                }
                else {
                    setResponseMsg(res.message);
                    setAlert('false');
                    setLoading(false)
                    setOpenAlert(true)
                    setTimeout(() => {
                        alertClose()
                    }, ALERT_DURATION);
                }
            })
        } catch (error) {
            console.error('User type creation failed:', error);
        }
    };

    const handleChange = (event) => {
        setCountry(event.target.value);
    };

    const countriesData = async () => {
        countryMetaData().then((res) => {
            if (res.statusCode === 200) {
                const countryManagementList = res.data.map(item => item.countryManagement);
                setCountryData(countryManagementList);
            }
        });
    }
    
    const solutionData = async () => {
        solutionMetaData().then((res) => {
            if (res.statusCode === 200) {
                setSolutionsData(res.data.solutions);
            }
        })
    }

    const handleFileChange = (e, onChange) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const imageTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
        const docTypes = [
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ];

        const validTypes = [...imageTypes, ...docTypes];

        if (!validTypes.includes(file.type)) {
            setResponseMsg("Only JPEG, PNG, WebP, DOC, DOCX, PPT, PPTX files are allowed.");
            setAlert('false');
            setOpenAlert(true);
            setTimeout(() => alertClose(), ALERT_DURATION);
            e.target.value = "";
            onChange(null);
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setResponseMsg("File size must be less than 5MB");
            setAlert('false');
            setOpenAlert(true);
            setTimeout(() => alertClose(), ALERT_DURATION);
            e.target.value = "";
            onChange(null);
            return;
        }

        if (imageTypes.includes(file.type)) {
            setPreviewImage(URL.createObjectURL(file));
        } else {
            setPreviewImage(null);
        }
        setSelectedFile(file);
        onChange(file);
        e.target.value = "";
    };

    return (
        <Box>
            <Grid container alignItems={'center'}>
                <Grid size={{ xs: 11, sm: 11, md: 11, lg: 11, xl: 11 }}>
                    <Typography className='h1'> {editMode ? "Update Initiative" : "Create Initiative"}</Typography>
                </Grid>
                <Grid size={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }} textAlign={'end'}>
                    <Icon icon="ei:arrow-left"
                        onClick={() => {
                            editMode
                                ? navigate(`/initiativeView/${id}?type=edit`)
                                : navigate('/initiative');
                        }} width="30" height="30" style={{ color: '#285D54', cursor: 'pointer' }} />
                </Grid>
            </Grid>

            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container mt={2} columnGap={4}>
                    <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}>
                        <Typography mb={1} style={{fontWeight:'700'}}>Solution <span style={{ color:'var(--red-color)' }}>*</span> </Typography>
                        <Grid container mt={2}>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <FormControl fullWidth error={!!errors.solutionId}>
                                    <InputLabel id="country-select-label">Select Solution</InputLabel>
                                    <Controller name="solutionId" control={control} defaultValue=""
                                        render={({ field, fieldState: { error } }) => (
                                            <Select {...field} label="Select Solution" >
                                                <MenuItem value="" disabled>Select Solution</MenuItem>
                                                {solutionsData.map((item) => (
                                                    <MenuItem key={item.name} value={item.id}>
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>)} />
                                    {solutionsData.length === 0 && <MenuItem>No Options</MenuItem>}

                                    {!!errors.solutionId && (<FormHelperText error>{errors.solutionId.message}</FormHelperText>)}
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container mt={2}>
                            <Typography mb={1} style={{fontWeight:'700'}}>Country <span style={{ color:'var(--red-color)' }}>*</span> </Typography>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <FormControl fullWidth error={!!errors.countryId}>
                                    <InputLabel id="country-select-label">Select Country</InputLabel>
                                    <Controller name="countryId" control={control} defaultValue=""
                                        render={({ field, fieldState: { error } }) => (
                                            <Select labelId="country-select-label" id="country-select" label='Select Country' value={country} onChange={handleChange} {...field} error={!!error} helperText={error ? error.message : ''}>
                                                <MenuItem value="" disabled>Select Country</MenuItem>
                                                {countryData.map((data) => (
                                                    <MenuItem key={data.name} value={data.id}>
                                                        {data.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>)} />
                                    {countryData.length === 0 && <MenuItem>No Options</MenuItem>}

                                    {!!errors.countryId && (<FormHelperText error>{errors.countryId.message}</FormHelperText>)}
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container columnGap={2} alignItems={'center'} mt={2}>
                            <Typography mb={1} style={{fontWeight:'700'}}>Label <span style={{ color:'var(--red-color)' }}>*</span> </Typography>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <FormControl fullWidth>
                                    <Controller name="label" control={control} defaultValue=""
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField  {...field} id="userType" label="label" type="text" variant="outlined"  {...field} error={!!error} helperText={error ? error.message : ''} />
                                        )} />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container mt={2} >
                            <Typography mb={1} style={{fontWeight:'700'}}>Description <span style={{ color:'var(--red-color)' }}>*</span> </Typography>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <FormControl fullWidth>
                                    <Controller name="description" control={control} rules={{ required: 'Description is required' }}
                                        render={({ field, fieldState }) => (
                                            <RichTextEditor value={field.value} onChange={field.onChange} error={fieldState.error} />
                                        )}
                                    />
                                    {errors.description && (
                                        <FormHelperText error>{errors.description.message}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }} >
                        <Typography mb={1} style={{fontWeight:'700'}}>Initiative <span style={{ color:'var(--red-color)' }}>*</span> </Typography>
                        <Grid container columnGap={2} alignItems={'center'} mt={2}>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <FormControl fullWidth>
                                    <Controller name="name" control={control} defaultValue=""
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField id="outlined-basic" placeholder='Enter Initiative Name' variant="outlined" {...field} error={!!error} helperText={error ? error.message : ''} />
                                        )} />
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container mt={2}>
                            <Typography mb={1} style={{fontWeight:'700'}}>Initiative Link <span style={{ color:'var(--red-color)' }}>*</span> </Typography>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <FormControl fullWidth>
                                    <Controller name="initiativeLink" control={control} defaultValue=""
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField  {...field} placeholder="Enter a Link" variant="outlined" error={!!error} helperText={error ? error.message : ''} />
                                        )}
                                    />

                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid container mt={2}>
                            <Typography mb={1} style={{fontWeight:'700'}}>Upload Image or Document</Typography>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <Controller name="" control={control} defaultValue=""
                                    render={({ field: { onChange }, fieldState: { error } }) => (
                                        <>
                                            <Box
                                                sx={{
                                                    border: '2px dashed #9e9e9e',
                                                    borderRadius: '8px',
                                                    padding: previewImage ? '8px' : '70px',
                                                    textAlign: 'center',
                                                    color: '#4b5563',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    height: 'auto',
                                                    minHeight: '200px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#fafafa'
                                                }} onClick={() => document.getElementById('file-input').click()}>
                                                {previewImage ? (
                                                    <>
                                                        <img src={previewImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '8px' }} />
                                                        <button
                                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedFile(null); setPreviewImage(null); }}
                                                            style={{
                                                                position: "absolute",
                                                                top: "-10px",
                                                                right: "-10px",
                                                                background: "white",
                                                                borderRadius: "50%",
                                                                border: "1px solid gray",
                                                                width: "30px",
                                                                height: "30px",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                            }}
                                                            title="Remove Icon">
                                                            ✕
                                                        </button>
                                                    </>
                                                ) : (
                                                    <Box>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 25 22" fill="none">
                                                            <path d="M16.5692 2.04947C16.7439 2.04971 16.8855 2.19117 16.8856 2.36588C16.8856 2.54071 16.744 2.68302 16.5692 2.68326C15.0912 2.68326 13.7637 3.67167 13.3427 5.08658C13.2965 5.24199 13.142 5.33396 12.9852 5.3063L12.9823 5.31021L12.9491 5.29947C12.7815 5.24948 12.6853 5.07363 12.7352 4.90591C13.2254 3.25884 14.7527 2.04947 16.5692 2.04947Z" fill="#285D54" stroke="#F9FFF9" stroke-width="0.107824" />
                                                            <path d="M20.2507 15.2529H18.6727C18.5275 15.2529 18.4097 15.1351 18.4097 14.9899C18.4097 14.8447 18.5275 14.7269 18.6727 14.7269H20.2507C22.4258 14.7269 24.1956 12.9572 24.1956 10.782C24.1956 8.60691 22.4258 6.83713 20.2507 6.83713H20.2127C20.1365 6.83713 20.064 6.80407 20.014 6.7464C19.964 6.68873 19.9415 6.61227 19.9524 6.53676C19.9759 6.37295 19.9877 6.20839 19.9877 6.04816C19.9877 4.16306 18.4539 2.62922 16.5688 2.62922C15.8354 2.62922 15.136 2.8584 14.5461 3.29215C14.4165 3.38739 14.2324 3.34512 14.1575 3.20254C12.4869 0.0214567 8.12356 -0.40573 5.86139 2.36153C4.90843 3.52734 4.534 5.04387 4.83404 6.52193C4.8671 6.68517 4.74217 6.83733 4.5763 6.83733H4.47091C2.29579 6.83733 0.526013 8.60711 0.526013 10.7822C0.526013 12.9574 2.29579 14.7271 4.47091 14.7271H6.04886C6.19406 14.7271 6.31185 14.8449 6.31185 14.9901C6.31185 15.1353 6.19407 15.2531 6.04886 15.2531H4.47091C2.00571 15.2531 0 13.2474 0 10.7822C0 8.38616 1.89467 6.42422 4.26446 6.31602C4.04186 4.78256 4.46753 3.23578 5.45412 2.02866C7.8761 -0.93433 12.5176 -0.602218 14.4797 2.70174C15.1057 2.30931 15.8219 2.10344 16.5687 2.10344C18.8526 2.10344 20.6624 4.04735 20.504 6.31847C22.852 6.45015 24.7215 8.40174 24.7215 10.782C24.7215 13.2474 22.7158 15.2529 20.2506 15.2529L20.2507 15.2529Z" fill="#285D54" />
                                                            <path d="M12.3257 8.21283C15.9808 8.21298 18.9546 11.1866 18.9546 14.8417C18.9544 18.4967 15.9807 21.4705 12.3257 21.4706C8.67061 21.4706 5.69693 18.4968 5.69678 14.8417C5.69678 11.1866 8.67047 8.21283 12.3257 8.21283ZM12.3257 8.84662C9.02015 8.84662 6.33057 11.5363 6.33057 14.8417C6.33072 18.1471 9.02029 20.8359 12.3257 20.8359C15.6309 20.8357 18.3197 18.147 18.3198 14.8417C18.3198 11.5363 15.631 8.84677 12.3257 8.84662Z" fill="#285D54" stroke="#F9FFF9" stroke-width="0.107824" />
                                                            <path d="M12.4456 12.2359C12.5817 12.2359 12.6926 12.346 12.6927 12.482V17.4879C12.6927 17.6241 12.5817 17.7349 12.4456 17.7349C12.3096 17.7348 12.1995 17.6239 12.1995 17.4879V12.482C12.1996 12.346 12.3097 12.236 12.4456 12.2359Z" fill="#285D54" stroke="#285D54" stroke-width="0.107824" />
                                                            <path d="M12.3104 12.2764C12.406 12.2135 12.5359 12.2245 12.62 12.3086L14.16 13.8486C14.2563 13.9449 14.2563 14.101 14.16 14.1973C14.112 14.2454 14.0489 14.2695 13.9862 14.2695C13.9233 14.2695 13.8595 14.2453 13.8114 14.1973L12.4452 12.8311L11.08 14.1973C10.9838 14.2936 10.8276 14.2934 10.7313 14.1973C10.635 14.101 10.635 13.9449 10.7313 13.8486L12.2714 12.3086L12.3104 12.2764Z" fill="#285D54" stroke="#285D54" stroke-width="0.107824" />
                                                        </svg>
                                                        <Typography variant="subtitle1" sx={{ mt: 1 }}>
                                                            Upload Image or{' '}
                                                            <span style={{ color: '#007C84', fontWeight: 500, cursor: 'pointer' }}>  Browse </span>
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Supported formats: JPEG, PNG, WebP, JPG (up to 5MB)
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>

                                            <input hidden type="file" id="file-input" accept=".jpeg,.jpg,.png,.webp,.doc,.docx,.ppt,.pptx" onChange={(e) => handleFileChange(e, onChange)} />
                                            {error && (
                                                <Typography variant="body2" color="error" mt={1}>
                                                    {error.message}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid container mb={2} mt={5}>
                    <Grid size={10}>
                        <Button variant="contained" className='resetbtn' onClick={() => { navigate('/initiative') }} style={{ display: editMode ? 'none' : 'block' }} >Cancel</Button>
                    </Grid>
                    <Grid size={2} textAlign={'end'}>
                        <Button variant="contained" className='createBtn' type="submit"> {editMode ? "Update Initiative" : "Create Initiative"}</Button>
                    </Grid>
                </Grid>
            </form>

            {loading && <Loader />}

            <Grid item xs={4} textAlign={'end'}>
                {(alert === 'true') && (
                    <Snackbar open={openAlert} autoHideDuration={6000} onClose={alertClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: '#B6FFC3',
                                py: 1.5,
                                px: 4,
                                borderRadius: '30px',
                                boxShadow: 3,
                                maxWidth: 400,
                                mr: -6
                            }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M10 20C9 20 8.25 19.1667 7.66667 18.5C7.33333 18.1667 7 17.75 6.83333 17.6667C6.58333 17.5833 6 17.5833 5.58333 17.5833C4.66667 17.6667 3.66667 17.6667 3 17C2.33333 16.3333 2.33333 15.25 2.41667 14.4167C2.41667 13.9167 2.5 13.4167 2.33333 13.1667C2.25 12.9167 1.83333 12.5833 1.5 12.3333C0.833333 11.75 0 11 0 10C0 9 0.833333 8.25 1.5 7.66667C1.83333 7.33333 2.25 7 2.33333 6.83333C2.41667 6.58333 2.41667 6 2.41667 5.58333C2.33333 4.66667 2.33333 3.66667 3 3C3.66667 2.33333 4.75 2.33333 5.58333 2.41667C6.08333 2.41667 6.58333 2.5 6.83333 2.33333C7.08333 2.25 7.41667 1.83333 7.66667 1.5C8.25 0.833333 9 0 10 0C11 0 11.75 0.833333 12.3333 1.5C12.6667 1.83333 13 2.25 13.1667 2.33333C13.4167 2.41667 14 2.41667 14.4167 2.41667C15.3333 2.33333 16.3333 2.33333 17 3C17.6667 3.66667 17.6667 4.75 17.5833 5.58333C17.5833 6.08333 17.5 6.58333 17.6667 6.83333C17.75 7.08333 18.1667 7.41667 18.5 7.66667C19.1667 8.25 20 9 20 10C20 11 19.1667 11.75 18.5 12.3333C18.1667 12.6667 17.75 13 17.6667 13.1667C17.5833 13.4167 17.5833 14 17.5833 14.4167C17.6667 15.3333 17.6667 16.3333 17 17C16.3333 17.6667 15.25 17.6667 14.4167 17.5833C13.9167 17.5833 13.4167 17.5 13.1667 17.6667C12.9167 17.75 12.5833 18.1667 12.3333 18.5C11.75 19.1667 11 20 10 20ZM6.25 15.9167C6.66667 15.9167 7.08333 16 7.5 16.0833C8.08333 16.3333 8.58333 16.8333 9 17.3333C9.33333 17.75 9.83333 18.25 10.0833 18.25C10.3333 18.25 10.8333 17.75 11.1667 17.3333C11.5833 16.8333 12.0833 16.3333 12.6667 16.0833C13.25 15.8333 14 15.8333 14.6667 15.9167C15.1667 15.9167 15.8333 16 16 15.8333C16.1667 15.6667 16.0833 15 16.0833 14.5C16 13.8333 16 13.0833 16.25 12.5C16.5 11.9167 17 11.4167 17.5 11C17.8333 10.75 18.3333 10.25 18.3333 10C18.3333 9.75 17.8333 9.25 17.4167 8.91667C16.9167 8.5 16.4167 8 16.1667 7.41667C15.9167 6.83333 15.9167 6.08333 16 5.41667C16 4.91667 16.0833 4.25 15.9167 4.08333C15.75 3.91667 15.0833 4 14.5833 4C13.9167 4.08333 13.1667 4.08333 12.5833 3.83333C12 3.58333 11.5 3.08333 11.0833 2.58333C10.75 2.16667 10.25 1.66667 10 1.66667C9.75 1.66667 9.25 2.16667 8.91667 2.58333C8.5 3.08333 8 3.58333 7.41667 3.83333C6.83333 4.08333 6.08333 4.08333 5.41667 4C4.91667 4 4.25 3.91667 4.08333 4.08333C3.91667 4.25 4 4.91667 4 5.41667C4.08333 6.08333 4.08333 6.83333 3.83333 7.41667C3.58333 8 3.08333 8.5 2.58333 8.91667C2.16667 9.25 1.66667 9.75 1.66667 10C1.66667 10.25 2.16667 10.75 2.58333 11.0833C3.08333 11.5 3.58333 12 3.83333 12.5833C4.08333 13.1667 4.08333 13.9167 4 14.5833C4 15.0833 3.91667 15.75 4.08333 15.9167C4.25 16.0833 4.91667 16 5.41667 16C5.66667 16 5.91667 15.9167 6.25 15.9167ZM9.16667 13.6667L6.08333 10.5833L7.25 9.41667L9.16667 11.3333L13.5833 6.91667L14.75 8.08333L9.16667 13.6667Z" fill="#00AD5E" />
                            </svg> &nbsp;&nbsp;
                            <Typography
                                sx={{ color: '#004d40', fontWeight: 600, fontSize: '0.95rem', flexGrow: 1, }}>
                                {responseMsg}
                            </Typography>
                        </Box>
                    </Snackbar>
                )}

                {(alert === 'false') && (
                    <Snackbar open={openAlert} autoHideDuration={6000} onClose={alertClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: '#FDEDED',
                                py: 1.5,
                                px: 4,
                                borderRadius: '30px',
                                boxShadow: 3,
                                maxWidth: 400,
                                mr: -6
                            }}
                        >
                            <Icon icon="solar:close-circle-linear" width="26" height="26" style={{ color:'var(--red-color)' }} /> &nbsp;&nbsp;
                            <Typography
                                sx={{
                                    color:'var(--red-color)',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    flexGrow: 1,
                                }}>
                                {responseMsg}
                            </Typography>
                        </Box>
                    </Snackbar>
                )}
            </Grid>
        </Box>
    )
}

export default InitiativeCreation
